// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  Checkbox,
  Switch,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";

import { gql, useMutation, useQuery } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { validateEmail, validateUsername } from "utils/validations";
import { UserContext } from "context/user";
import { validateDNI } from "utils/validations";
import dayjs from "dayjs";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflowY: "auto",
  p: 3,
};

const DEFAULT_EMPLOYEE_PERMISSION_IDS = ["201", "211", "221", "231"];
const validateTempPassword = (value) => (value || "").length >= 6;

export default function ModalUsuarios({ open, handleClose, refetch, selectedUsuario, tipo }) {
  const [usuario, setUsuario] = useState();
  const [sectionTab, setSectionTab] = useState(0);
  const [permissionTab, setPermissionTab] = useState(0);
  const [permissionSource, setPermissionSource] = useState("");
  const hasPermissions = (selectedUsuario?.tipoUser || tipo) !== "Administrador";
  const { handleSnackbar } = useContext(MessageManager);
  const { user, verificarAcceso } = useContext(UserContext);

  const handleChanges = (e) => {
      setUsuario({
        ...usuario,
        [e.target.name]: e.target.value,
    });
  };

  const { data: usuarios } = useQuery(
    gql`
      query getUsuarios {
        usuarios {
          id
          username
          email
          tipoUser
          nombre
          apellido
          idsPermisos
        }
      }
    `,
    { skip: !open, fetchPolicy: "no-cache" }
  );
  const copySources = (usuarios?.usuarios || []).filter(
    u => u.tipoUser === "Empleado" && String(u.id) !== String(selectedUsuario?.id)
  );
  const usernameExists = usuarios?.usuarios?.some(
    (u) => u.username === usuario?.username && u.id !== usuario?.id
  );
  const hasEmployeeChanges = selectedUsuario
    ? !(
        usuario?.username === selectedUsuario.username &&
        usuario?.email === selectedUsuario.email &&
        usuario?.nombre === selectedUsuario.nombre &&
        usuario?.apellido === selectedUsuario.apellido &&
        usuario?.dni === selectedUsuario.dni &&
        usuario?.fechaNacimiento === selectedUsuario.fechaNacimiento &&
        usuario?.genero === selectedUsuario.genero &&
        Boolean(usuario?.horarioTrabajoHabilitado) === Boolean(selectedUsuario.horarioTrabajoHabilitado) &&
        JSON.stringify(usuario?.horariosTrabajo || []) === JSON.stringify(selectedUsuario.horariosTrabajo || []) &&
        JSON.stringify(usuario?.idsPermisos || []) === JSON.stringify(selectedUsuario.idsPermisos || []) &&
        !usuario?.tempPassword
      )
    : true;

  const { data: permisos, loading: loadingPermisos, error: errorPermisos } = useQuery(gql`
    query permisosAdministrables { permisosAdministrables { id nombre pantalla } }
  `, { skip: !open });
  const canManagePermissions = verificarAcceso(246) && (user?.tipoUser === "Administrador" || String(selectedUsuario?.id) !== String(user?.id));
  const groups = [...new Set((permisos?.permisosAdministrables || []).map(p => p.pantalla))];
  const togglePermission = (permission, checked) => {
    setPermissionSource("");
    const ids = new Set((usuario?.idsPermisos || []).map(String));
    if (checked) {
      ids.add(permission.id);
      const read = permisos.permisosAdministrables.find(p => p.pantalla === permission.pantalla && p.nombre === "Consultar");
      if (read) ids.add(read.id);
    } else if (permission.nombre === "Consultar") {
      permisos.permisosAdministrables.filter(p => p.pantalla === permission.pantalla).forEach(p => ids.delete(p.id));
    } else ids.delete(permission.id);
    setUsuario(prev => ({ ...prev, idsPermisos: [...ids] }));
  };

  const [saveUsuario, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveUser($input: UserInput!) {
        saveUser(input: $input) {
          id
        }
      }
    `
  );

  const changeSchedule = (index, field, value) => setUsuario(prev => ({ ...prev, horariosTrabajo: prev.horariosTrabajo.map((item, n) => n === index ? { ...item, [field]: value } : item) }));
  const invalidSchedule = usuario?.horarioTrabajoHabilitado && (!(usuario.horariosTrabajo || []).length || usuario.horariosTrabajo.some(h => !h.desde || !h.hasta || h.desde === h.hasta));

  const handleSave = () => {
    const nextTempPassword =
      !selectedUsuario && usuario?.password
        ? usuario.password
        : !selectedUsuario?.tempPassword && usuario?.tempPassword
          ? usuario.tempPassword
          : undefined;
    saveUsuario({
      variables: {
        input: {
          ...usuario,
          tipoUser: selectedUsuario?.tipoUser || tipo,
          username: usuario?.username,
          idsPermisos: canManagePermissions && (selectedUsuario?.tipoUser || tipo) !== "Administrador" ? (usuario?.idsPermisos || []).map(String).filter(id => permisos.permisosAdministrables.some(p => p.id === id)) : undefined,
          horarioTrabajoHabilitado: hasPermissions && canManagePermissions ? Boolean(usuario?.horarioTrabajoHabilitado) : undefined,
          horariosTrabajo: hasPermissions && canManagePermissions ? usuario?.horariosTrabajo || [] : undefined,
          tempPassword: nextTempPassword,
        },
      },
    })
      .then(() => {
        refetch();
        handleSnackbar("Usuario guardado correctamente", "success");
        handleClose();
      })
      .catch((e) => {
        handleSnackbar(e.message || "Error al guardar el usuario", "error");
      });
  };

  useEffect(() => {
    setSectionTab(0);
    setPermissionTab(0);
    setPermissionSource("");
    if (selectedUsuario) {
      setUsuario({
        id: selectedUsuario.id,
        username: selectedUsuario.username,
        email: selectedUsuario.email,
        nombre: selectedUsuario.nombre,
        apellido: selectedUsuario.apellido,
        dni: selectedUsuario.dni,
        fechaNacimiento: selectedUsuario.fechaNacimiento,
        genero: selectedUsuario.genero,
        horarioTrabajoHabilitado: Boolean(selectedUsuario.horarioTrabajoHabilitado),
        horariosTrabajo: selectedUsuario.horariosTrabajo || [],
        idsPermisos: selectedUsuario.idsPermisos || [],
        tempPassword: "",
      });
    } else {
      setUsuario({ password: "", horarioTrabajoHabilitado: false, horariosTrabajo: [], idsPermisos: DEFAULT_EMPLOYEE_PERMISSION_IDS });
    }
  }, [selectedUsuario, open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">
                  {selectedUsuario
                    ? `Editar ${tipo.toLowerCase()}`
                    : `Agregar ${tipo.toLowerCase()}`}
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            {hasPermissions && (
              <Tabs variant="scrollable" scrollButtons="auto" value={sectionTab} onChange={(_, value) => setSectionTab(value)} aria-label="Secciones de la ficha del empleado" sx={{ mt: 2, "& .MuiTab-root": { minHeight: "44px !important" } }}>
                <Tab id="employee-data-tab" aria-controls="employee-data-panel" label="Datos personales" />
                <Tab id="employee-permissions-tab" aria-controls="employee-permissions-panel" label="Permisos" />
                <Tab id="employee-schedule-tab" aria-controls="employee-schedule-panel" label="Horarios de trabajo" />
              </Tabs>
            )}
            {sectionTab === 0 && (
            <SoftBox py={3} role="tabpanel" id="employee-data-panel" aria-labelledby={hasPermissions ? "employee-data-tab" : undefined}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="username">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Nombre de usuario
                      <SoftTypography variant="caption" fontWeight="regular" color="text">
                        &nbsp;(Usuario para login)
                      </SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <Tooltip
                    placement="bottom"
                    open={
                      usuario?.username?.length > 0 &&
                      usernameExists
                    }
                    title={
                      usernameExists
                        ? "El nombre de usuario ya existe"
                        : "Ingrese un nombre de usuario"
                    }
                  >
                    <SoftInput
                      label="username"
                      placeholder="Ingrese el nombre de usuario"
                      type="text"
                      name="username"
                      value={usuario?.username || ""}
                      error={
                        usuario?.username?.length > 0 &&
                        (!validateUsername(usuario?.username) || usernameExists)
                      }
                      onChange={handleChanges}
                      disabled={Boolean(selectedUsuario?.username)}
                      readOnly={selectedUsuario?.username ? true : false}
                    />
                  </Tooltip>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="username">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Email de usuario
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="email"
                    placeholder="Ingrese el email de usuario"
                    type="text"
                    name="email"
                    value={usuario?.email || ""}
                    error={usuario?.email?.length > 0 && !validateEmail(usuario?.email)}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="nombre">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Nombre
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="nombre"
                    placeholder="Ingrese el nombre del usuario"
                    type="text"
                    name="nombre"
                    value={usuario?.nombre || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="apellido">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Apellido
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="apellido"
                    placeholder="Ingrese el apellido del usuario"
                    type="text"
                    name="apellido"
                    value={usuario?.apellido || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="dni">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      DNI
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="dni"
                    placeholder="Ingrese el dni del usuario"
                    type="text"
                    name="dni"
                    value={usuario?.dni || ""}
                    error={usuario?.dni?.length > 0 && !validateDNI(usuario?.dni)}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="fechaNacimiento">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Fecha de nacimiento
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="fechaNacimiento"
                    type="date"
                    name="fechaNacimiento"
                    value={usuario?.fechaNacimiento || ""}
                    error={
                      usuario?.fechaNacimiento &&
                      dayjs(usuario?.fechaNacimiento).isAfter(dayjs().subtract(17, "year"))
                    }
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="genero">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Genero
                    </SoftTypography>
                  </InputLabel>
                  <Select name="genero" value={usuario?.genero || -1} onChange={handleChanges}>
                    <MenuItem value={-1} disabled>
                      Seleccione uno
                    </MenuItem>
                    <MenuItem value={"Masculino"}>Masculino</MenuItem>
                    <MenuItem value={"Femenino"}>Femenino</MenuItem>
                    <MenuItem value={"Otro"}>Otro</MenuItem>
                    <MenuItem value={"Prefiero no decirlo"}>Prefiero no decirlo</MenuItem>
                  </Select>
                </Grid>
                {!selectedUsuario && (
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="password">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Contraseña temporal
                        <SoftTypography variant="caption" fontWeight="regular" color="text">
                          &nbsp;(6 caract. min.)
                        </SoftTypography>
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <Tooltip
                      placement="bottom"
                      open={usuario?.password?.length > 0 && !validateTempPassword(usuario?.password)}
                      title="Mínimo 6 caracteres"
                    >
                      <SoftInput
                        label="password"
                        placeholder="Ingrese la contraseña temporal"
                        type="text"
                        name="password"
                        value={usuario?.password || ""}
                        error={
                          usuario?.password?.length > 0 && !validateTempPassword(usuario?.password)
                        }
                        onChange={handleChanges}
                      />
                    </Tooltip>
                  </Grid>
                )}
                {selectedUsuario && (
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="tempPassword">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Contraseña temporal
                        <SoftTypography variant="caption" fontWeight="regular" color="text">
                          &nbsp;(6 caract. min.)
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <Tooltip
                      placement="bottom"
                      open={
                        !selectedUsuario?.tempPassword &&
                        usuario?.tempPassword?.length > 0 &&
                        !validateTempPassword(usuario?.tempPassword)
                      }
                      title="Mínimo 6 caracteres"
                    >
                      <SoftInput
                        label="tempPassword"
                        placeholder={
                          selectedUsuario?.tempPassword
                            ? "Contraseña temporal vigente"
                            : "Ingrese una nueva contraseña temporal"
                        }
                        type="text"
                        name="tempPassword"
                        value={
                          selectedUsuario?.tempPassword
                            ? selectedUsuario?.tempPassword || ""
                            : usuario?.tempPassword || ""
                        }
                        error={
                          !selectedUsuario?.tempPassword &&
                          usuario?.tempPassword?.length > 0 &&
                          !validateTempPassword(usuario?.tempPassword)
                        }
                        onChange={handleChanges}
                        readOnly={Boolean(selectedUsuario?.tempPassword)}
                        disabled={Boolean(selectedUsuario?.tempPassword) || !verificarAcceso(245)}
                      />
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </SoftBox>
            )}
            {hasPermissions && sectionTab === 1 && (
              <SoftBox py={3} role="tabpanel" id="employee-permissions-panel" aria-labelledby="employee-permissions-tab">
                <SoftTypography variant="body2" mb={2}>Elegí qué puede hacer en cada módulo. Las acciones incluyen acceso de consulta.</SoftTypography>
                {loadingPermisos && <CircularProgress size={24} aria-label="Cargando permisos" />}
                {errorPermisos && <SoftTypography color="error" variant="body2">No se pudieron cargar los permisos. Cerrá y volvé a abrir la ficha.</SoftTypography>}
                {canManagePermissions && copySources.length > 0 && (
                  <SoftBox mb={2}>
                    <InputLabel id="copy-permissions-label" sx={{ fontSize: "0.875rem", whiteSpace: "normal", lineHeight: 1.4 }}>Copiar permisos de otro usuario</InputLabel>
                    <Select labelId="copy-permissions-label" value={permissionSource} displayEmpty fullWidth disabled={loadingPermisos || loadingSave || Boolean(errorPermisos)} onChange={e => {
                      const source = copySources.find(u => u.id === e.target.value);
                      if (source) {
                        setPermissionSource(source.id);
                        setUsuario(prev => ({ ...prev, idsPermisos: (source.idsPermisos || []).map(String).filter(id => permisos.permisosAdministrables.some(p => p.id === id)) }));
                      }
                    }}>
                      <MenuItem value="" disabled>Seleccionar usuario</MenuItem>
                      {copySources.map(u => (
                        <MenuItem key={u.id} value={u.id}>{[u.nombre, u.apellido].filter(Boolean).join(" ") || u.username} ({u.username})</MenuItem>
                      ))}
                    </Select>
                  </SoftBox>
                )}
                {groups.length > 0 && (
                  <>
                    <Tabs value={permissionTab} onChange={(_, value) => setPermissionTab(value)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label="Módulos de permisos" sx={{
                      bgcolor: "transparent", borderRadius: 0, borderBottom: 1, borderColor: "divider", p: 0, minHeight: 48,
                      "& .MuiTabs-indicator": { height: 3, bottom: 0, bgcolor: "primary.main", boxShadow: "none", transition: "none" },
                      "& .MuiTab-root": { flex: "none", minHeight: 48, px: 2, py: 1, fontWeight: 600 },
                      "& .MuiTab-root.Mui-selected": { color: "primary.main !important" },
                      "& .MuiTab-root:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
                    }}>
                      {groups.map((group, index) => <Tab key={group} id={`permissions-tab-${index}`} aria-controls={`permissions-panel-${index}`} label={group} />)}
                    </Tabs>
                    <SoftBox role="tabpanel" id={`permissions-panel-${permissionTab}`} aria-labelledby={`permissions-tab-${permissionTab}`} sx={{ pt: 2 }}>
                        <FormGroup sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, columnGap: 2 }}>
                          {permisos.permisosAdministrables.filter(p => p.pantalla === groups[permissionTab]).map(p => (
                            <FormControlLabel key={p.id} sx={{ display: "flex", minHeight: 44, m: 0, "& .MuiFormControlLabel-label": { lineHeight: 1.4 } }} label={p.nombre} control={
                              <Checkbox checked={(usuario?.idsPermisos || []).map(String).includes(p.id)} disabled={!canManagePermissions || loadingSave || (String(usuario?.id) === String(user?.id) && user?.tipoUser !== "Administrador")} onChange={(_, checked) => togglePermission(p, checked)} />
                            } />
                          ))}
                        </FormGroup>
                    </SoftBox>
                  </>
                )}
              </SoftBox>
            )}
            {hasPermissions && sectionTab === 2 && <SoftBox py={3} role="tabpanel" id="employee-schedule-panel" aria-labelledby="employee-schedule-tab">
              <FormControlLabel label="Restringir acceso por horario" control={<Switch checked={Boolean(usuario?.horarioTrabajoHabilitado)} disabled={!canManagePermissions} onChange={(_, checked) => setUsuario({ ...usuario, horarioTrabajoHabilitado: checked })} />} />
              <SoftTypography variant="button" display="block" mb={2}>{usuario?.horarioTrabajoHabilitado ? "Puede acceder únicamente en las franjas cargadas. Hora de San Luis. Si Hasta es anterior a Desde, la franja termina al día siguiente." : "Puede acceder cualquier día y a cualquier hora."}</SoftTypography>
              {usuario?.horarioTrabajoHabilitado && <>
                {(usuario?.horariosTrabajo || []).map((horario, index) => <Grid container spacing={2} key={index} alignItems="end" mb={2}>
                  <Grid item xs={12} sm={4}><SoftTypography variant="caption" fontWeight="bold" display="block" mb={1}>Día</SoftTypography><Select fullWidth value={horario.dia} disabled={!canManagePermissions} onChange={e => changeSchedule(index, 'dia', Number(e.target.value))} inputProps={{ 'aria-label': `Día de franja ${index + 1}` }}>{['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'].map((dia, n) => <MenuItem key={n} value={n}>{dia}</MenuItem>)}</Select></Grid>
                  {['desde','hasta'].map(field => <Grid item xs={5} sm={3} key={field}><SoftTypography component="label" htmlFor={`horario-${field}-${index}`} variant="caption" fontWeight="bold" display="block" mb={1}>{field === 'desde' ? 'Desde' : 'Hasta'}</SoftTypography><SoftInput id={`horario-${field}-${index}`} type="time" value={horario[field]} disabled={!canManagePermissions} onChange={e => changeSchedule(index, field, e.target.value)} /></Grid>)}
                  <Grid item xs={2} sm={2}><SoftButton iconOnly variant="outlined" color="primary" aria-label={`Quitar franja ${index + 1}`} disabled={!canManagePermissions} onClick={() => setUsuario({ ...usuario, horariosTrabajo: usuario.horariosTrabajo.filter((_, n) => n !== index) })}><Icon>delete</Icon></SoftButton></Grid>
                </Grid>)}
                <SoftButton variant="outlined" color="primary" disabled={!canManagePermissions} onClick={() => setUsuario({ ...usuario, horariosTrabajo: [...(usuario.horariosTrabajo || []), { dia: 1, desde: '08:00', hasta: '16:00' }] })}><Icon>add</Icon>&nbsp;Agregar franja</SoftButton>
                {invalidSchedule && <SoftTypography variant="caption" color="error" display="block" mt={2}>Cargá al menos una franja completa con horas distintas.</SoftTypography>}
              </>}
            </SoftBox>}
            <SoftBox
              display={{
                xs: "flex-row",
                sm: "flex",
              }}
              justifyContent="space-between"
              alignItems="center"
            >
              <SoftBox>
                {sectionTab === 0 && verificarAcceso(245) &&
                  selectedUsuario &&
                  selectedUsuario?.tempPassword &&
                  selectedUsuario?.tipoUser !== "Administrador" && (
                    <Tooltip title="Copiar contraseña temporal" placement="top">
                      <SoftButton
                        color="gobSecondary"
                        
                        onClick={() => {
                          if (selectedUsuario.tempPassword) {
                            navigator.clipboard.writeText(selectedUsuario.tempPassword);
                          }
                          handleSnackbar("Contraseña temporal copiada", "success");
                        }}
                        fullWidth={{
                          xs: true,
                          sm: false,
                        }}
                      >
                        <Icon sx={{ fontWeight: "light" }}>copy</Icon>
                        &nbsp;Copiar contraseña temporal
                      </SoftButton>
                    </Tooltip>
                  )}
              </SoftBox>
              <SoftBox
                display={{
                  xs: "flex-row",
                  sm: "flex",
                }}
                justifyContent="end"
                alignItems="center"
              >
                <SoftBox
                  mr={{
                    xs: 0,
                    sm: 2,
                  }}
                  mt={{
                    xs: 2,
                    sm: 0,
                  }}
                >
                  <SoftButton
                    color="primary"
                    
                    onClick={handleClose}
                    fullWidth={{
                      xs: true,
                      sm: false,
                    }}
                  >
                    <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                    &nbsp;Cancelar
                  </SoftButton>
                </SoftBox>
                <SoftBox
                  mt={{
                    xs: 2,
                    sm: 0,
                  }}
                >
                  <SoftButton
                    color="gobSecondary"
                    
                      disabled={
                        invalidSchedule || loadingSave || loadingPermisos || Boolean(errorPermisos) ||
                        (selectedUsuario
                          ? !usuario?.email ||
                            !usuario?.username ||
                            !usuario?.nombre ||
                            !usuario?.apellido ||
                            (!selectedUsuario?.tempPassword &&
                              usuario?.tempPassword &&
                              !validateTempPassword(usuario?.tempPassword)) ||
                            !validateEmail(usuario?.email) ||
                            !validateUsername(usuario?.username) ||
                            usernameExists ||
                            !hasEmployeeChanges
                          : !usuario?.email ||
                            !usuario?.username ||
                            !usuario?.nombre ||
                            !usuario?.apellido ||
                            !usuario?.password ||
                            !validateTempPassword(usuario?.password) ||
                            (usuario?.dni && !validateDNI(usuario?.dni)) ||
                            (usuario?.fechaNacimiento &&
                              dayjs(usuario?.fechaNacimiento).isAfter(
                                dayjs().subtract(17, "year")
                              )) ||
                            !validateEmail(usuario?.email) ||
                            !validateUsername(usuario?.username) ||
                            usernameExists)
                      }
                    onClick={handleSave}
                    fullWidth={{
                      xs: true,
                      sm: false,
                    }}
                  >
                    {loadingSave ? (
                      <CircularProgress
                        size={15}
                        sx={{
                          marginRight: 0.5,
                        }}
                        color="inherit"
                      />
                    ) : (
                      <Icon sx={{ fontWeight: "light" }}>save</Icon>
                    )}
                    &nbsp;Guardar
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalUsuarios.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  selectedUsuario: PropTypes.object,
  tipo: PropTypes.string,
};
